import { Link, useNavigate } from "@tanstack/react-router";
import { MessageSquare, Plus, X } from "lucide-react";

import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useThreads } from "@/hooks/useThreads";
import { cn } from "@/lib/utils";

interface ThreadSidebarProps {
  activeThreadId?: string;
  onClose: () => void;
}

export function ThreadSidebar({ activeThreadId, onClose }: ThreadSidebarProps) {
  const { threads, createThread, isLoading } = useThreads();
  const navigate = useNavigate();
  // Thread lock (frontend-doctrine §4.16). HIDDEN, not disabled: a greyed-out
  // button invites a click and then explains nothing.
  const { mode } = useOnboardingStatus();
  const newChatHidden = mode === "onboarding";

  async function handleNewChat() {
    const threadId = await createThread({ title: "New Chat" });
    void navigate({ to: "/chat/$threadId", params: { threadId: threadId as string } });
  }

  return (
    <div className="flex h-full flex-col border-r border-black/10 bg-white dark:border-white/10 dark:bg-surface-dark">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-black/10 px-4 py-3 dark:border-white/10">
        <h3 className="text-sm font-bold text-black dark:text-white">Threads</h3>
        <button
          onClick={onClose}
          className="rounded-lg p-1 text-black/40 transition hover:bg-black/5 hover:text-black dark:text-white/40 dark:hover:bg-white/5 dark:hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* New chat — suppressed during the first-run ritual */}
      {!newChatHidden && (
        <div className="p-3">
          <button
            onClick={handleNewChat}
            className="flex w-full items-center gap-2 rounded-lg border border-black/10 px-3 py-2 text-sm font-bold text-black/70 transition hover:bg-black/5 dark:border-white/10 dark:text-white/70 dark:hover:bg-white/5"
          >
            <Plus className="h-4 w-4" />
            New Chat
          </button>
        </div>
      )}

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto px-3">
        {isLoading ? (
          <div className="py-4 text-center text-xs text-black/40 dark:text-white/40">Loading...</div>
        ) : threads && threads.length > 0 ? (
          <div className="space-y-1">
            {threads.map((thread) => (
              <Link
                key={thread._id}
                to="/chat/$threadId"
                params={{ threadId: thread._id as string }}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition",
                  activeThreadId === thread._id
                    ? "border-l-[3px] border-brand-coral bg-brand-coral/5 pl-[9px] font-bold text-brand-coral"
                    : "text-black/60 hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white",
                )}
              >
                <MessageSquare className="h-4 w-4 shrink-0" />
                <span className="truncate">{thread.title}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="py-4 text-center text-xs text-black/40 dark:text-white/40">No threads yet</div>
        )}
      </div>
    </div>
  );
}
